/**
 * A tool call's arguments as a person reads them: "Query: altruist OR Mazi",
 * "Max results: 10" — not the JSON the model wrote.
 *
 * Rows show one or two telling values beside the heading and every value on
 * expansion. Commands and scripts stay code; this is for everything else.
 *
 * @module components/chat/toolInputText
 */

export interface ToolInputLine {
  /** The argument's own name, as the tool took it. */
  readonly key: string;
  readonly label: string;
  readonly value: string;
}

const VALUE_MAX_LENGTH = 600;
const PREVIEW_MAX_LENGTH = 120;
/** Keys that carry plumbing, not what the call was about. */
const QUIET_KEYS: ReadonlySet<string> = new Set(["version", "cursor", "accountScope"]);

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/** `max_results` / `maxResults` → "Max results". */
export function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_\-.]+/g, " ")
    .trim()
    .toLowerCase();
  return words.length > 0 ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

function clip(value: string, max: number): string {
  const singleLine = value.replace(/\s+/g, " ").trim();
  return singleLine.length > max ? `${singleLine.slice(0, max - 1)}…` : singleLine;
}

/** JSON text an argument carries (plugin actions pass theirs as a string). */
function parseJsonText(value: string): unknown {
  const text = value.trim();
  if (!text.startsWith("{") && !text.startsWith("[")) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

function scalarText(value: unknown): string | null {
  if (typeof value === "string") return value.trim().length > 0 ? value.trim() : null;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return null;
}

/** Deep enough for `{ filters: { date: { after } } }`; deeper reads as a count. */
const NESTING_LIMIT = 4;
const ARRAY_ITEMS_SHOWN = 5;
/**
 * Arguments that are an envelope around the real ones, passed as JSON text:
 * a plugin action's `arguments`. Their keys read at the call's own level;
 * any other JSON-looking string keeps its own label.
 */
const ENVELOPE_KEYS: ReadonlySet<string> = new Set(["arguments"]);

function valueText(value: unknown, depth = 0): string | null {
  const scalar = scalarText(value);
  if (scalar !== null) return scalar;
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    if (depth >= NESTING_LIMIT) return `${value.length} ${value.length === 1 ? "item" : "items"}`;
    // Each item as itself — a recipient's address, not "1 item" — up to a few.
    const shown = value
      .slice(0, ARRAY_ITEMS_SHOWN)
      .map((item) => {
        const text = valueText(item, depth + 1);
        return text !== null && asRecord(item) ? `(${text})` : text;
      })
      .filter((item): item is string => item !== null);
    if (shown.length === 0) return `${value.length} ${value.length === 1 ? "item" : "items"}`;
    const more = value.length - ARRAY_ITEMS_SHOWN;
    return more > 0 ? `${shown.join(", ")} +${more} more` : shown.join(", ");
  }
  const record = asRecord(value);
  if (record) {
    const entries = Object.entries(record);
    if (depth >= NESTING_LIMIT) {
      return entries.length === 0
        ? null
        : `${entries.length} ${entries.length === 1 ? "field" : "fields"}`;
    }
    // A nested object reads as its own short "key: value" run.
    const parts = entries
      .map(([key, nested]) => {
        const text = valueText(nested, depth + 1);
        return text === null ? null : `${humanizeKey(key)}: ${text}`;
      })
      .filter((part): part is string => part !== null);
    return parts.length > 0 ? parts.join(", ") : null;
  }
  return null;
}

/**
 * Every argument of a call as a labelled line. An argument that is itself
 * JSON text (a plugin action's `arguments`) is opened up in place, so its
 * keys read at the same level as the call's own.
 */
export function toolInputLines(
  input: unknown,
  options: { readonly omit?: ReadonlySet<string> } = {},
): ToolInputLine[] {
  const record = asRecord(input);
  if (!record) return [];
  const lines: ToolInputLine[] = [];
  for (const [key, raw] of Object.entries(record)) {
    if (options.omit?.has(key)) continue;
    const parsed = typeof raw === "string" ? parseJsonText(raw) : undefined;
    const nested = asRecord(parsed);
    if (nested && ENVELOPE_KEYS.has(key)) {
      lines.push(...toolInputLines(nested, options));
      continue;
    }
    const text = valueText(parsed !== undefined ? parsed : raw);
    if (text === null) continue;
    lines.push({ key, label: humanizeKey(key), value: clip(text, VALUE_MAX_LENGTH) });
  }
  return lines;
}

export function formatToolInputLines(lines: ReadonlyArray<ToolInputLine>): string {
  return lines.map((line) => `${line.label}: ${line.value}`).join("\n");
}

/** The first value worth reading beside a row's heading. */
export function toolInputPreview(input: unknown): string | null {
  const first = toolInputLines(input).find((line) => !QUIET_KEYS.has(line.key));
  return first ? clip(first.value, PREVIEW_MAX_LENGTH) : null;
}

/**
 * A one-line summary that is JSON (the adapter's fallback when a call has no
 * obvious query or path) read as text. The summary is cut at 200 characters,
 * so a partial object is read field by field rather than parsed.
 */
export function jsonSummaryPreview(summary: string): string | null {
  const text = summary.trim();
  const parsed = parseJsonText(text);
  // A whole array (search terms, ids) reads as its items.
  if (Array.isArray(parsed)) {
    const items = valueText(parsed);
    return items === null ? null : clip(items, PREVIEW_MAX_LENGTH);
  }
  if (!text.startsWith("{")) return null;
  if (parsed !== undefined) return toolInputPreview(parsed);
  for (const match of text.matchAll(/"([^"\\]+)"\s*:\s*"((?:[^"\\]|\\.)*)"/g)) {
    const key = match[1]!;
    if (QUIET_KEYS.has(key)) continue;
    const value = match[2]!.replace(/\\(.)/g, "$1").trim();
    if (value.length > 0 && !value.startsWith("{")) return clip(value, PREVIEW_MAX_LENGTH);
  }
  return null;
}

export function looksLikeJson(value: string | undefined): boolean {
  const text = value?.trim() ?? "";
  return text.startsWith("{") || text.startsWith("[");
}
